import React, { useEffect, useState } from "react";

import { useGetDatasetMappersData } from "api/api-hooks/dataset-mapper-hooks";
import { useGetDatasetsInfo } from "api/api-hooks/datasets-hooks";
import Plotly, { LayoutAxis } from "plotly.js";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import Plot from "react-plotly.js";
import { useResizeDetector } from "react-resize-detector";
import copyChartToClipBoard from "./chartUtils";
import { clipboardIcon, forkIcon, logScaleIcon } from "./icons";
import { AxesConfig, DatasetChartProps, PlotDataEx } from "./interfaces";
import { splitLongWord, splitStringToLines } from "./utils";

const commonAxisConfig = {
  showspikes: true,
  spikemode: "toaxis",
  automargin: true,
  tickformat: "f",
} as Partial<LayoutAxis>;

const secondaryAxisConfig = {
  ...commonAxisConfig,
  overlaying: "y",
  showgrid: false,
};

const xAxisCommonConfig = {
  spikemode: "toaxis",
  showspikes: true,
};

const layoutConfig = {
  margin: {
    l: 0,
    r: 0,
    b: 30,
    t: 20,
  },
  hoverlabel: {
    align: "left",
  },
} as Plotly.Layout;

export default function BasicChart({
  plotData,
  props,
}: {
  plotData: PlotDataEx[];
  props?: DatasetChartProps;
}) {
  const { width, height, ref } = useResizeDetector();
  const [traces, setTraces] = useState<Array<Partial<PlotDataEx>>>([]);
  const [axesConfig, setAxesConfig] = useState<AxesConfig>({});
  const [axesGrouped, setAxesGrouped] = useState<boolean>(props?.toggleTracesGrouping ?? true);
  const [isScatterChart, setIsScatterChart] = useState<boolean>(false);
  const [logScale, setLogScale] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [renderCount, setRenderCount] = useState<number>(0);
  const identifiers = plotData.map(dataset => dataset.identifier);
  const [initialTraces, setInitialTraces] = useState<Array<Partial<PlotDataEx>>>(plotData);

  const filteredIdentifiers = identifiers.filter(id => id !== undefined) as (string | null)[];
  const { data: datasetInfo, isLoading: isDatasetsInfoLoading } =
    useGetDatasetsInfo(filteredIdentifiers);

  const { data: mapperDataResponse, isLoading: isMapperDataLoading } = useGetDatasetMappersData();

  useEffect(() => {
    if (props?.selectedChartType === "SCATTER") {
      setIsScatterChart(true);
    } else {
      setIsScatterChart(false);
    }
  }, [props?.selectedChartType]);

  useEffect(() => {
    loadData();
  }, [plotData]);

  useEffect(() => {
    setRenderCount(renderCount + 1);
  }, [width, traces, loaded, axesConfig, axesGrouped]);

  useEffect(() => {
    if (initialTraces.length > 0) {
      for (let i = 0; i < initialTraces.length; i++) {
        if (initialTraces[i]?.name && !initialTraces[i]?.name.includes(`(${initialTraces[i]?.unit})`)) {
          const newTraceName = `${initialTraces[i].name} (${initialTraces[i].unit})`;
          initialTraces[i].name = newTraceName;
        }
      }      
      if (axesGrouped) {
        groupByUnitTraces(initialTraces);
      } else {
        ungroupTraces(initialTraces);
      }
        if (!isDatasetsInfoLoading || !isMapperDataLoading) {
          loadData();
        }

      setTraces(initialTraces);
    }
    setLoaded(true);
  }, [initialTraces, axesGrouped, width]);

  useEffect(() => {
    if (!isMapperDataLoading || !isDatasetsInfoLoading) {
      loadData();
    }
  }, [datasetInfo]);

  const onRefresh = () => {
    if (!isMapperDataLoading || !isDatasetsInfoLoading) {
      loadData();
    }
  };

  const toggleGroupingHandler = () => {
    setAxesGrouped(axesGrouped => !axesGrouped);
  };

  const loadData = () => {
    setLoading(true);

    const newPlotData = plotData;

    if (props?.selectedChartType === "LINE") {
      if (
        Array.isArray(mapperDataResponse) &&
        mapperDataResponse.length >= 1 &&
        Array.isArray(newPlotData) &&
        newPlotData.length >= 1
      ) {
        for (let i = 0; i < newPlotData.length; i += 1) {
          const currentDataset = datasetInfo?.find(
            dataset => dataset.identifier === newPlotData[i].identifier
          );
          if (!currentDataset) continue;
          const currentMapper = mapperDataResponse.find(
            mapper => mapper.id == currentDataset?.stringValueMapper
          );
          if (!currentMapper) continue;
          newPlotData[i].y = newPlotData[i].y.map(value => {
            if (typeof value === "string") return value;
            const mappedValue = currentMapper.mappings[value as number] || value;
            if (typeof mappedValue === "string") {
              const lines = splitStringToLines(mappedValue);
              const mappedLines = lines.map((line, index) => {
                if (index === 0 || line.length === 15) {
                  return line;
                }
                return splitLongWord(line);
              });
              return mappedLines.join("<br>");
            }
            return mappedValue;
          });
          newPlotData[i].unit = currentMapper.title;
        }
      }
    }
    setTimeout(() => {
      setInitialTraces(newPlotData);
    }, 600)
    setLoading(false);
  };

  const groupByUnitTraces = (data: Array<Partial<PlotDataEx>>) => {
    const unitMap = new Map<string, string>();
    const axes = axesConfig;
    const domain = [0, 1];

    for (let i = 0; i < data.length; i++) {
      const unit = data[i].unit!!;
      const trace = data[i];
      if (!unitMap.has(unit)) {
        const axCount = unitMap.size + 1;
        if (axCount == 1) {
          unitMap.set(unit, "y");
          axes.yaxis = createAxisConfig(axCount, unit!, domain);
        } else {
          unitMap.set(unit, `y${axCount}`);

          axes[`yaxis${axCount}`] = createAxisConfig(axCount, unit!, domain);
        }
      }
      trace.yaxis = unitMap.get(unit)!!;
    }

    axes.xaxis = { ...xAxisCommonConfig, domain } as LayoutAxis;

    setAxesConfig(axes);
  };

  const ungroupTraces = (data: Array<Partial<PlotDataEx>>) => {
    const axes = axesConfig;
    const domain = [0, 1];

    for (let i = 0; i < data.length; i += 1) {
      const { unit } = data[i];
      const trace = data[i];
      const axis = i + 1;
      if (axis === 1) {
        axes.yaxis = createAxisConfig(axis, unit!, domain);
      } else {
        axes[`yaxis${axis}`] = createAxisConfig(axis, unit!, domain);
        trace.yaxis = `y${axis}`;
      }
    }

    axes.xaxis = { ...xAxisCommonConfig, domain } as LayoutAxis;

    setAxesConfig(axes);
  };

  const createAxisConfig = (
    axis: number,
    unit: string,
    domain: Array<number>
  ): Plotly.LayoutAxis => {
    if (axis === 1) {
      return {
        title: {
          text: unit,
          standoff: 10,
        },
        ...commonAxisConfig,
      } as LayoutAxis;
    }
    if (axis === 2) {
      return {
        title: {
          text: unit,
          standoff: 10,
        },
        side: "right",
        anchor: "x",
        ...secondaryAxisConfig,
      } as LayoutAxis;
    }
    let axOffset;
    const pxVal = 100 / width!!;
    const offVal = pxVal * 0.75;

    // Left
    if (axis % 2 == 0) {
      axOffset = Math.floor(axis / 2) * offVal - offVal * 2;
      domain[0] = Math.max(0, axOffset) + offVal;
    }
    // Right
    else {
      axOffset = 1 - Math.floor(axis / 2) * offVal + offVal;

      if (axOffset > 0) {
        domain[1] = axOffset - offVal - offVal / 3;
      }
    }

    return {
      title: {
        text: unit,
        standoff: 4,
      },
      position: axOffset,
      side: "left",
      anchor: "free",
      ...secondaryAxisConfig,
    } as LayoutAxis;
  };

  const getChartHeight = (): number | undefined => {
    if (props?.height != null) {
      return props?.height as number;
    }
    if (props?.height ? props?.height <= 0 : false) {
      return height;
    }
    return 400;
  };

  const chartConfig: Partial<Plotly.Config> = {
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: [
      {
        title: "Toggle trace grouping",
        name: "Toggle trace grouping",
        icon: forkIcon,
        click: toggleGroupingHandler,
      },
      {
        title: "Copy chart to Clipboard",
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
      ...(isScatterChart
        ? [
            {
              name: "Log Scale",
              title: "Toggle Y-axis Log Scale",
              icon: logScaleIcon,
              click: () => {
                setLogScale(logScale => !logScale);
              },
            },
          ]
        : []),
    ],
  };
  return (
    <div ref={ref} className="p-m-2" style={{ minHeight: "300px", overflow: "hidden" }}>
      {loaded && !props?.hideRefresh && (
        <div className="flex align-items-center p-mx-2">
          <Button
            icon="pi pi-refresh"
            type="button"
            loading={loading}
            onClick={onRefresh}
            className="p-mr-2"
          />
        </div>
      )}
      {!loaded || props?.loading || isDatasetsInfoLoading || isMapperDataLoading || loading ? (
        <div className="flex justify-content-center p-mb-4">
          <ProgressSpinner
            style={{ width: "20rem", height: "20rem" }}
            strokeWidth="4"
            animationDuration="1s"
          />
        </div>
      ) : (
        <Plot
          className="ea-plotly-chart"
          config={{ ...chartConfig }}
          data={[...traces]}
          layout={{
            ...axesConfig,
            ...layoutConfig,
            margin: { t: 20, b: 0, l: 0, r: 0, pad: 10 },
            legend: props?.legend || { orientation: "h" },
            xaxis: props?.xaxisName ? { title: { text: props.xaxisName } } : {},
            yaxis:
              props?.selectedChartType === "LINE"
                ? { ...axesConfig.yaxis }
                : { ...axesConfig.yaxis, type: logScale && isScatterChart ? "log" : "linear" },
            width: width!!,
            height: getChartHeight(),
            hovermode: "closest",
          }}
        />
      )}
    </div>
  );
}
